import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { CacheStatus, Manuscript, ModelRef } from "../lib/types.js";
import type { SidebarSection } from "../workspace/Sidebar.js";
import { CollectionPanel, type CollectionRefs } from "./CollectionPanel.js";
import { NotesPanel } from "./NotesPanel.js";
import { VaultPanel } from "./VaultPanel.js";
import { InsightsPanel } from "./InsightsPanel.js";
import { RelationshipsPanel } from "../relationships/RelationshipsPanel.js";
import { AudiobookPanel } from "../audiobook/AudiobookPanel.js";
import { PublishPanel } from "./PublishPanel.js";
import { BookCachePanel } from "./BookCachePanel.js";
import { ResearchPanel } from "./ResearchPanel.js";
import { TimelinePanel } from "./TimelinePanel.js";
import { CharactersPanel } from "./CharactersPanel.js";
import { LocationsPanel } from "./LocationsPanel.js";
import { StoryBiblePanel } from "./StoryBiblePanel.js";
import { COLLECTION_CONFIGS } from "./config.js";

interface Props {
  projectId: string;
  section: SidebarSection;
  manuscript: Manuscript;
  onOpenScene: (sceneId: string) => void;
  onOpenSection?: (section: SidebarSection) => void;
  onManuscriptChanged: () => Promise<void>;
  onCacheChanged?: () => void;
  onIntelChanged?: () => void;
  cacheStatus?: CacheStatus | null;
  cacheBuilding?: boolean;
  cacheProgress?: { processed: number; remaining: number } | null;
  onBuildCache?: (model: ModelRef) => void;
  charsOrganizing?: boolean;
  charsVersion?: number;
  onOrganizeCharacters?: (model: ModelRef) => void;
  focusId?: string | null;
}

/** Routes a non-manuscript sidebar section to its workspace panel. */
export function SectionView({ projectId, section, manuscript, onOpenScene, onOpenSection, onManuscriptChanged, onCacheChanged, onIntelChanged, cacheStatus, cacheBuilding, cacheProgress, onBuildCache, charsOrganizing, charsVersion, onOrganizeCharacters, focusId }: Props) {
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);

  // Location references are needed by the Timeline's location picker.
  useEffect(() => {
    let cancelled = false;
    api
      .listCollection(projectId, "locations")
      .then((res) => {
        if (!cancelled) {
          setLocations(res.items.map((l) => ({ id: l.id, name: String(l.name ?? "Untitled") })));
        }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [projectId, section]);

  const refs: CollectionRefs = {
    scenes: [...manuscript.scenes]
      .sort((a, b) => a.position - b.position)
      .map((s) => ({ id: s.id, title: s.title })),
    locations
  };

  if (section === "notes") {
    return <NotesPanel projectId={projectId} focusId={focusId} />;
  }
  if (section === "vault") {
    return <VaultPanel projectId={projectId} onOpenScene={onOpenScene} onRestored={onManuscriptChanged} />;
  }
  if (section === "insights") {
    return <InsightsPanel projectId={projectId} manuscript={manuscript} onOpenScene={onOpenScene} onOpenSection={onOpenSection} onIntelChanged={onIntelChanged} />;
  }
  if (section === "relationships") {
    return <RelationshipsPanel projectId={projectId} onOpenScene={onOpenScene} />;
  }
  if (section === "audiobook") {
    return <AudiobookPanel projectId={projectId} manuscript={manuscript} />;
  }
  if (section === "publish") {
    return <PublishPanel projectId={projectId} />;
  }
  if (section === "cache") {
    return (
      <BookCachePanel
        projectId={projectId}
        status={cacheStatus ?? null}
        building={cacheBuilding ?? false}
        progress={cacheProgress ?? null}
        onRefresh={onCacheChanged}
        onBuild={onBuildCache}
      />
    );
  }
  if (section === "research") {
    return <ResearchPanel projectId={projectId} onOpenScene={onOpenScene} onOpenSection={onOpenSection} />;
  }
  if (section === "timeline") {
    return <TimelinePanel projectId={projectId} refs={refs} onOpenScene={onOpenScene} />;
  }
  if (section === "locations") {
    return <LocationsPanel projectId={projectId} onOpenScene={onOpenScene} focusId={focusId} />;
  }
  if (section === "storyBible") {
    const chapterTitles = new Map(manuscript.chapters.map((c) => [c.id, c.title]));
    const sceneRefs = [...manuscript.scenes]
      .sort((a, b) => a.position - b.position)
      .map((s) => ({ id: s.id, title: s.title, chapterId: s.chapterId, chapterTitle: chapterTitles.get(s.chapterId) ?? "" }));
    return <StoryBiblePanel projectId={projectId} sceneRefs={sceneRefs} onOpenScene={onOpenScene} focusId={focusId} />;
  }
  if (section === "characters") {
    return (
      <CharactersPanel
        projectId={projectId}
        refs={refs}
        onOpenScene={onOpenScene}
        organizing={charsOrganizing ?? false}
        reloadKey={charsVersion ?? 0}
        onOrganize={onOrganizeCharacters}
      />
    );
  }

  const config = COLLECTION_CONFIGS[section];
  if (!config) {
    return null;
  }
  return (
    <CollectionPanel
      key={section}
      projectId={projectId}
      config={config}
      refs={refs}
      onOpenScene={onOpenScene}
      focusId={focusId}
    />
  );
}
